'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { Button } from '@/design-system';

interface WelcomeModalProps {
  isOpen: boolean;
  /** Called on skip, close and "Get started"; the provider marks the modal as seen. */
  onClose: () => void;
}

/* Full-bleed illustration header for each screen, drawn with theme tokens so it
   adapts to dark/light. Each screen carries one of the 42A logo colors (lime
   squares, cyan bars, pink triangle) as its accent. Labels come through i18n
   like all other copy. */

function TrackArt({ visibility, mentions }: { visibility: string; mentions: string }) {
  return (
    <svg width="330" height="170" viewBox="0 0 270 140" aria-hidden="true">
      {/* 42A icon mark (four lime squares) */}
      <g transform="translate(10, 14)">
        <rect x="0" y="0" width="7" height="7" fill="var(--color-lime)" />
        <rect x="9" y="0" width="7" height="7" fill="var(--color-lime)" />
        <rect x="0" y="9" width="7" height="7" fill="var(--color-lime)" />
        <rect x="9" y="9" width="7" height="7" fill="var(--color-lime)" />
      </g>
      <rect x="34" y="14" width="106" height="52" rx="10" fill="var(--color-surface)" stroke="var(--color-border-light)" />
      <text x="48" y="36" fontSize="9" fill="var(--color-text-muted)" style={{ fontFamily: 'inherit', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{visibility}</text>
      <text x="48" y="57" fontSize="19" fontWeight="600" fill="var(--color-lime)" style={{ fontFamily: 'inherit' }}>72</text>
      <polygon points="76,55 79,50 82,55" fill="var(--color-success)" />
      <text x="85" y="57" fontSize="9" fill="var(--color-success)" style={{ fontFamily: 'inherit' }}>4.2</text>
      <rect x="152" y="14" width="106" height="52" rx="10" fill="var(--color-surface)" stroke="var(--color-border-light)" />
      <text x="166" y="36" fontSize="9" fill="var(--color-text-muted)" style={{ fontFamily: 'inherit', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{mentions}</text>
      <text x="166" y="57" fontSize="19" fontWeight="600" fill="var(--color-text-primary)" style={{ fontFamily: 'inherit' }}>318</text>
      <rect x="10" y="76" width="248" height="52" rx="10" fill="var(--color-surface)" stroke="var(--color-border-light)" />
      {/* competitor trend (cyan, dashed) kept fully inside the card */}
      <path
        d="M26 120 C64 119 104 113 144 115 S216 108 246 106"
        fill="none"
        stroke="var(--color-cyan)"
        strokeWidth="1.8"
        strokeDasharray="4 3"
        opacity="0.65"
      />
      {/* your brand (lime) */}
      <path
        d="M26 118 C58 114 76 105 104 107 S160 95 190 97 S232 88 246 86"
        fill="none"
        stroke="var(--color-lime)"
        strokeWidth="2.2"
      />
      <circle cx="246" cy="86" r="3.5" fill="var(--color-lime)" />
    </svg>
  );
}

function ProjectArt({ brands, prompts, schedule }: { brands: string; prompts: string; schedule: string }) {
  return (
    <svg width="330" height="165" viewBox="0 0 280 140" aria-hidden="true">
      {/* one card per logo color: lime squares / cyan bars / pink clock */}
      <rect x="14" y="46" width="72" height="48" rx="10" fill="var(--color-surface)" stroke="var(--color-lime)" />
      <text x="50" y="64" fontSize="9" fill="var(--color-lime)" textAnchor="middle" style={{ fontFamily: 'inherit' }}>{brands}</text>
      <g transform="translate(41, 71)">
        <rect x="0" y="0" width="8" height="8" rx="1" fill="var(--color-lime)" />
        <rect x="10" y="0" width="8" height="8" rx="1" fill="var(--color-lime)" opacity="0.45" />
        <rect x="0" y="10" width="8" height="8" rx="1" fill="var(--color-lime)" opacity="0.45" />
        <rect x="10" y="10" width="8" height="8" rx="1" fill="var(--color-lime)" opacity="0.45" />
      </g>
      <rect x="104" y="46" width="72" height="48" rx="10" fill="var(--color-surface)" stroke="var(--color-cyan)" />
      <text x="140" y="64" fontSize="9" fill="var(--color-cyan)" textAnchor="middle" style={{ fontFamily: 'inherit' }}>{prompts}</text>
      <rect x="122" y="72" width="36" height="4" rx="2" fill="var(--color-cyan)" opacity="0.9" />
      <rect x="122" y="80" width="24" height="4" rx="2" fill="var(--color-cyan)" opacity="0.45" />
      <rect x="194" y="46" width="72" height="48" rx="10" fill="var(--color-surface)" stroke="var(--color-pink)" />
      <text x="230" y="64" fontSize="9" fill="var(--color-pink)" textAnchor="middle" style={{ fontFamily: 'inherit' }}>{schedule}</text>
      {/* clock */}
      <circle cx="230" cy="79" r="9" fill="none" stroke="var(--color-pink)" strokeWidth="2" />
      <path d="M230 74 v5 l4 3" fill="none" stroke="var(--color-pink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M86 70 h18 M176 70 h18" stroke="var(--color-border-light)" strokeWidth="2" strokeDasharray="3 3" />
    </svg>
  );
}

/* Third screen: three "do something" tiles — Vitals, Agents, Tasks — each with
   a small action illustration in its 42A logo color, replacing the old wall of
   feature tags. */
function GrowArt({ actions }: { actions: { vitals: string; agents: string; tasks: string } }) {
  return (
    <div className="flex items-stretch justify-center gap-3 px-4">
      <ActionTile color="var(--color-lime)" label={actions.vitals}>
        {/* gauge — measure & improve AI-readiness */}
        <path d="M6 25 A13 13 0 0 1 32 25" fill="none" stroke="var(--color-border-light)" strokeWidth="3.5" strokeLinecap="round" />
        <path d="M6 25 A13 13 0 0 1 24 12.8" fill="none" stroke="var(--color-lime)" strokeWidth="3.5" strokeLinecap="round" />
        <circle cx="19" cy="25" r="2.6" fill="var(--color-lime)" />
        <path d="M19 25 L27 16" stroke="var(--color-lime)" strokeWidth="2.4" strokeLinecap="round" />
      </ActionTile>
      <ActionTile color="var(--color-pink)" label={actions.agents}>
        {/* agent — a friendly bot that goes to work for you */}
        <rect x="8" y="12" width="22" height="16" rx="5" fill="none" stroke="var(--color-pink)" strokeWidth="2.4" />
        <circle cx="15" cy="20" r="2" fill="var(--color-pink)" />
        <circle cx="23" cy="20" r="2" fill="var(--color-pink)" />
        <path d="M19 6 v6" stroke="var(--color-pink)" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="19" cy="5" r="2.2" fill="var(--color-pink)" />
      </ActionTile>
      <ActionTile color="var(--color-cyan)" label={actions.tasks}>
        {/* tasks — a checklist you work through */}
        <rect x="9" y="7" width="20" height="24" rx="3.5" fill="none" stroke="var(--color-cyan)" strokeWidth="2.4" />
        <path d="M13 14 l2.4 2.4 L20 12" fill="none" stroke="var(--color-cyan)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13 24 l2.4 2.4 L20 22" fill="none" stroke="var(--color-cyan)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
        <path d="M22 15 h4 M22 25 h4" stroke="var(--color-cyan)" strokeWidth="2.2" strokeLinecap="round" opacity="0.7" />
      </ActionTile>
    </div>
  );
}

function ActionTile({ color, label, children }: { color: string; label: string; children: React.ReactNode }) {
  return (
    <div
      className="flex flex-col items-center gap-2 rounded-xl border px-5 py-4 bg-[var(--color-surface)]"
      style={{ borderColor: color }}
    >
      <svg width="38" height="34" viewBox="0 0 38 34" aria-hidden="true">
        {children}
      </svg>
      <span className="text-xs font-semibold" style={{ color }}>{label}</span>
    </div>
  );
}

/* One 42A logo color per screen: lime (squares) → cyan (bars) → pink (triangle).
   The accent drives the step label, the active dot, and the header glow. */
const SCREENS = [
  { key: 'track', accent: 'var(--color-lime)', glow: 'var(--color-lime-muted)' },
  { key: 'project', accent: 'var(--color-cyan)', glow: 'rgba(0, 212, 255, 0.14)' },
  { key: 'grow', accent: 'var(--color-pink)', glow: 'rgba(233, 30, 124, 0.13)' },
] as const;

export default function WelcomeModal({ isOpen, onClose }: WelcomeModalProps) {
  const t = useTranslations('welcome');
  const [step, setStep] = useState(0);
  // Initial focus target: the primary CTA, not the close button (which would
  // otherwise get Headless UI's autofocus and show a focus ring on open).
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  // Always start from the first screen when (re)opened.
  useEffect(() => {
    if (isOpen) setStep(0);
  }, [isOpen]);

  const isLast = step === SCREENS.length - 1;
  const { key, accent, glow } = SCREENS[step];

  const art =
    key === 'track' ? (
      <TrackArt visibility={t('art.visibility')} mentions={t('art.mentions')} />
    ) : key === 'project' ? (
      <ProjectArt brands={t('art.brands')} prompts={t('art.prompts')} schedule={t('art.schedule')} />
    ) : (
      <GrowArt
        actions={{
          vitals: t('art.actions.vitals'),
          agents: t('art.actions.agents'),
          tasks: t('art.actions.tasks'),
        }}
      />
    );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose} initialFocus={primaryButtonRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-[560px] transform overflow-hidden rounded-2xl text-left align-middle shadow-xl transition-all"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {/* 42A logo-color strip: lime → cyan → pink */}
                <div
                  className="h-1"
                  style={{
                    background:
                      'linear-gradient(90deg, var(--color-lime) 0%, var(--color-cyan) 50%, var(--color-pink) 100%)',
                  }}
                />

                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('skip')}
                  className="absolute top-4 right-4 z-10 p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-[var(--color-text-muted)]" />
                </button>

                {/* Illustration header, tinted by the current screen's accent */}
                <div
                  className="h-[220px] grid place-items-center border-b transition-[background] duration-300"
                  style={{
                    borderColor: 'var(--color-border)',
                    background: `radial-gradient(120% 140% at 85% -20%, ${glow}, transparent 60%), radial-gradient(100% 120% at 0% 110%, rgba(0, 212, 255, 0.08), transparent 55%), var(--color-surface-elevated)`,
                  }}
                >
                  {/* Re-mount on step change so the fade-in replays */}
                  <div key={key} className="animate-fade-in">
                    {art}
                  </div>
                </div>

                {/* Copy */}
                <div key={`body-${key}`} className="px-9 pt-7 pb-7 text-center animate-fade-in">
                  <p
                    className="text-xs font-semibold uppercase tracking-[0.14em] mb-2.5"
                    style={{ color: accent }}
                  >
                    {t(`screens.${key}.label`)}
                  </p>
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-semibold mb-2.5"
                    style={{ color: 'var(--color-text-primary)', textWrap: 'balance' }}
                  >
                    {t(`screens.${key}.title`)}
                  </Dialog.Title>
                  <p className="text-sm leading-relaxed max-w-[52ch] mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
                    {t(`screens.${key}.body`)}
                  </p>
                </div>

                {/* Footer: skip, dots, back/next */}
                <div className="flex items-center justify-between px-9 pb-7">
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-sm px-1 py-1.5 transition-colors text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                    style={{ visibility: isLast ? 'hidden' : 'visible' }}
                  >
                    {t('skip')}
                  </button>

                  <div className="flex gap-2" role="tablist" aria-label={t('stepLabel', { current: step + 1, total: SCREENS.length })}>
                    {SCREENS.map((screen, i) => (
                      <button
                        key={screen.key}
                        type="button"
                        role="tab"
                        aria-selected={i === step}
                        aria-label={t('stepLabel', { current: i + 1, total: SCREENS.length })}
                        onClick={() => setStep(i)}
                        className="h-2 rounded-full transition-all duration-200"
                        style={{
                          width: i === step ? 24 : 8,
                          background: i === step ? screen.accent : 'var(--color-border-light)',
                        }}
                      />
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setStep(step - 1)}
                      style={{ visibility: step === 0 ? 'hidden' : 'visible' }}
                    >
                      {t('back')}
                    </Button>
                    <Button
                      ref={primaryButtonRef}
                      size="sm"
                      className="shadow-glow-lime"
                      onClick={() => (isLast ? onClose() : setStep(step + 1))}
                    >
                      {isLast ? t('getStarted') : t('next')}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
